'use strict'

import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Box from '@components/kit/box'
import Text from '@components/kit/text'
import Loading from '@components/kit/loading'
import { useMemberOpportunitiesStore } from '@store/MemberOpportunities'
import RoomMessages from './room-messages'
import RoomInput from './room-input'
import { useChatRoom } from './use-chat-room'
import { ChatContext } from '../chat.web'

interface ChatRoomProps {
  roomId?: number
}

// what a screen can hand over when it opens a member's chat
export type ChatLocationState = {
  // typed into the input for the professional to review and send
  draftMessage?: string
  // the dashboard opportunity (`_id`) the draft was written for
  opportunityId?: number
}

function ChatRoom({ roomId }: ChatRoomProps) {
  const { userId, userInfo } = useContext(ChatContext)
  const {
    isOnline,
    isLoading,
    isOtherTyping,
    isReady,
    shouldBeSendingMessages,
    allMessages,
    hasNextPage,
    loadMoreMessages,
    handleSendText,
    handleSendTyping,
    handleSendFile,
    handleGetDocumentUrl,
    memberTimezoneOffset,
    memberDisplayName
  } = useChatRoom(userId, roomId, userInfo)

  const location = useLocation()
  const navigate = useNavigate()
  const [draftMessage, setDraftMessage] = useState(
    () => (location.state as ChatLocationState | null)?.draftMessage ?? ''
  )
  // kept with its member: the room stays mounted when the professional
  // switches to another member
  const [draftOpportunity, setDraftOpportunity] = useState(() => {
    const opportunityId = (location.state as ChatLocationState | null)
      ?.opportunityId
    return opportunityId !== undefined ? { opportunityId, userId } : null
  })
  const [sentMessageId, setSentMessageId] = useState<string | null>(null)
  const [statusFailed, setStatusFailed] = useState(false)

  // taken off the history entry once read, so a reload or a later visit to
  // this member does not type it again
  useEffect(() => {
    const state = location.state as ChatLocationState | null
    if (state?.draftMessage || state?.opportunityId !== undefined) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null
      })
    }
  }, [])

  const sendText = (text: string) => {
    const messageId = handleSendText(text)
    // the input remounts on reconnect, and must not bring a sent draft back
    setDraftMessage('')
    if (draftOpportunity?.userId === userId && messageId) {
      setSentMessageId(messageId)
    }
  }

  // the opportunity counts as sent to the member once the server confirms the
  // first message sent from its chat; a failed send, or a failed status
  // update, waits for the next message
  useEffect(() => {
    if (!draftOpportunity || !sentMessageId) {
      return
    }
    const sent = allMessages.find(message => message.id === sentMessageId)
    if (!sent || sent.isPending || sent.sendFailed) {
      return
    }
    setSentMessageId(null)
    useMemberOpportunitiesStore
      .getState()
      .updateOpportunityStatus(
        draftOpportunity.userId,
        draftOpportunity.opportunityId,
        'SENT_TO_MEMBER'
      )
      .then(result => {
        setStatusFailed(!result.success)
        if (result.success) {
          setDraftOpportunity(null)
        }
      })
  }, [allMessages, draftOpportunity, sentMessageId])

  if (!isReady || (isLoading && allMessages.length === 0)) {
    return (
      <Box flex='1 1 auto' justifyContent='center' alignItems='center'>
        <Loading />
      </Box>
    )
  }

  return (
    <Box
      flex='1 1 0'
      flexDirection='column'
      bg='white'
      style={{ minHeight: 0, overflow: 'hidden' }}
    >
      <Box flex='1 1 0' style={{ minHeight: 0, overflow: 'hidden' }}>
        <RoomMessages
          key={userId}
          userId={userId}
          messages={allMessages}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          shouldBeSendingMessages={shouldBeSendingMessages}
          onLoadMore={loadMoreMessages}
          onGetDocumentUrl={handleGetDocumentUrl}
          memberTimezoneOffset={memberTimezoneOffset}
          memberDisplayName={memberDisplayName}
        />
      </Box>

      {statusFailed && draftOpportunity?.userId === userId && (
        <Box flex='0 0 auto' px='16' py='04'>
          <Text font='text.12.medium' textColor='proDangerDeep'>
            The opportunity could not be marked as sent. Your next message
            will try again.
          </Text>
        </Box>
      )}

      {isOtherTyping && (
        <Box flex='0 0 auto' px='16' py='04'>
          <Text font='text.12.medium' textColor='shade05'>
            Member is typing...
          </Text>
        </Box>
      )}

      <Box flex='0 0 auto'>
        {shouldBeSendingMessages ? (
          <RoomInput
            initialText={draftMessage}
            onSendText={sendText}
            onSendTyping={handleSendTyping}
            onSendFile={handleSendFile}
          />
        ) : (
          <Box
            backgroundColor={isOnline ? 'mintGreen' : 'walrusRed050'}
            flexDirection='row'
            alignItems='center'
            paddingY='20'
            width='100%'
          >
            <Text
              flex='1 1 auto'
              font='text.16.semibold'
              textColor={isOnline ? 'walrusGreen500' : 'red'}
              textAlign='center'
            >
              {isOnline
                ? 'Reconnecting...\nIf this takes too long, please refresh the page.'
                : 'You are currently offline.\nPlease check your network connection.'}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ChatRoom
